import httpClient from './httpClient'
import { SOFTPHONE_STATE_CHANGE_REASON, STATE_CHANGE_DETAIL_MAX_LENGTH } from '@/services/softphone/stateChangeReason'

export { SOFTPHONE_STATE_CHANGE_REASON }

const END_POINT = '/customerresources'

/**
 * `detail` is free text that only reaches the server's request log - the Telnyx error behind a
 * pause the widget made itself - so support can see why without the agent's browser console. It
 * writes no campaign note: softphone pauses are presence changes, which NotesService skips.
 */
function reasonConfig(reason, detail) {
  const params = {}

  if (reason) {
    params.reason = reason
  }

  if (detail) {
    params.detail = String(detail).slice(0, STATE_CHANGE_DETAIL_MAX_LENGTH)
  }

  return Object.keys(params).length ? { params } : undefined
}

/**
 * Every softphone endpoint is scoped by account, and an account id that never loaded used to be
 * interpolated straight into the path - `/customerresources/undefined/softphone/...`, answered with
 * a 403 that says nothing about the real problem. Fail here instead, with something the agent can
 * act on.
 */
function requireAccountId(accountId) {
  if (!accountId) {
    throw new Error('Your account details are not loaded yet, so the softphone cannot start. Close the softphone window and go live again.')
  }

  return accountId
}

const SoftphoneAPI = {
  /**
   * Get a short lived Telnyx WebRTC `login_token` for the signed-in agent's softphone credential in
   * this account (ringba-x #2724).
   *
   * @param {string} accountId - The account ID
   * @returns {Promise<{ data: { token: string, expiresAt: string } }>}
   */
  async getWebRtcToken(accountId) {
    return await httpClient.get(`${END_POINT}/${requireAccountId(accountId)}/softphone/webrtc-token`)
  },

  /**
   * Go live on ONE softphone campaign. The server pauses every other active softphone campaign that
   * rings the signed-in agent in this account, then resumes this one - the one-live-campaign rule is
   * enforced there, not here.
   *
   * @param {string} accountId - The account ID
   * @param {string} campaignId - The campaign to go live on
   * @param {{ reason?: string }} [options] - Optional SOFTPHONE_STATE_CHANGE_REASON; omitted = agent action
   */
  async goLive(accountId, campaignId, { reason } = {}) {
    return await httpClient.put(`${END_POINT}/${requireAccountId(accountId)}/softphone/campaigns/${campaignId}/go-live`, null, reasonConfig(reason))
  },

  /**
   * Take the signed-in agent offline in this account: the server pauses every active softphone
   * campaign that rings them. No campaign id on purpose - whatever is live stops ringing, including a
   * campaign this window never knew about.
   *
   * @param {string} accountId - The account ID
   * @param {{ reason?: string, detail?: string }} [options] - Optional SOFTPHONE_STATE_CHANGE_REASON
   *   (omitted = agent action) and the error text behind a pause the widget made itself
   */
  async goOffline(accountId, { reason, detail } = {}) {
    return await httpClient.put(`${END_POINT}/${requireAccountId(accountId)}/softphone/campaigns/go-offline`, null, reasonConfig(reason, detail))
  },

  /**
   * Tells the server the signed-in agent is still there. Sent on a fixed interval for as long as
   * the widget is live - see `startHeartbeat` in the softphone store, which arms it, and
   * `_clearHeartbeatTimer` (private to that module), which tears it down. The server auto-pauses
   * the campaign if this stops arriving for long enough, so a single failed request here is not
   * itself an error the caller needs to react to; the next tick retries.
   *
   * @param {string} accountId - The account ID
   */
  async heartbeat(accountId) {
    return await httpClient.put(`${END_POINT}/${requireAccountId(accountId)}/softphone/campaigns/heartbeat`)
  },

  /**
   * Tells the server the widget AUTOMATICALLY declined a call as busy because it was already
   * holding another one, so the declined call is not counted towards the campaign's unanswered-call
   * auto-pause. Never sent for a Decline the agent clicked, nor without `ringbaCallId` - the only
   * key RingbaX matches on. Fire-and-forget from the store: a lost
   * report costs one unanswered-call penalty, which is the behaviour without it.
   *
   * @param {string} accountId - The account ID
   * @param {string} campaignId - The live campaign the declined call was routed for
   * @param {{ ringbaCallId: ?string, callerNumber: ?string, declinedAt: string, heldCallState: ?string, telnyxCallId: ?string }} report
   */
  async reportBusyDecline(accountId, campaignId, { ringbaCallId, callerNumber, declinedAt, heldCallState, telnyxCallId }) {
    return await httpClient.put(
      `${END_POINT}/${requireAccountId(accountId)}/softphone/campaigns/${campaignId}/busy-declines`,
      { ringbaCallId, callerNumber, declinedAt, heldCallState, telnyxCallId }
    )
  }
}

export default SoftphoneAPI
